import React, { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import { TranslationContext } from '../contexts/TranslationContext'
import { getAssets } from '../selectors'
import { getConflictedFileExtensions } from '../helpers/incompatibleFileExtensions'
import { isVideo } from '~/Util/assets'
import { onlyUnique } from '~/Util/arrays'
import { deepEqual } from '~/Util/deepEqual'
import { setErrorMessage } from '~/actions/mainView'
import { selectBehaviourSettings } from '../selectors/mainView'
import { BehaviourSettings } from '../config/constants/editorSettings'
import { useAction } from './utils'


export const useCanAddItemToTimeline = () => {
  const settings = useSelector(selectBehaviourSettings)
  const allAssets = useSelector(getAssets)
  const { t } = React.useContext(TranslationContext)
  const onDenied = useAction(setErrorMessage)

  return useCallback((incompatibleExtensions, assets) => {
    const hasOfflineClip = allAssets.some(asset => asset.mediaDeleted)

    let reasonText = ''
    const conflictedExtensions = getConflictedFileExtensions(
      incompatibleExtensions ?? [],
      allAssets.map(({ filetype }) => filetype).filter(onlyUnique)
    )

    if (hasOfflineClip) {
      reasonText = t('MESSAGE_BOX_PLEASE_DELETE_OFFLINE_CLIP')
    }

    if (!isEmpty(conflictedExtensions)) {
      reasonText = t(assets.length === 1
        ? 'MESSAGE_BOX_REMOVE_EXISTING'
        : 'MESSAGE_BOX_INCOMPATIBLE')
    }

    if (reasonText) {
      onDenied(reasonText)
      return {
        canAdd: false,
        allValid: false,
        validAssets: [],
        skippedAssets: assets.map(asset => ({ asset, reason: reasonText })),
        skippedSummary: { [reasonText]: assets.length },
      }
    }

    const validAssets = []
    const skippedAssets = []
    const skippedSummary = {}

    const addSkipped = (asset, reasonKey) => {
      const reasonText = t(reasonKey)
      skippedAssets.push({ asset, reason: reasonText })

      if (!skippedSummary[reasonText]) {
        skippedSummary[reasonText] = 0
      }
      skippedSummary[reasonText] += 1
    }

    let referenceVideo = allAssets.find(asset => isVideo(asset))

    const getVideoCodec = str => str?.split(',')?.[0]?.trim() ?? ''

    for (const asset of assets) {
      if (!isVideo(asset)) {
        validAssets.push(asset)
        continue
      }

      if (!referenceVideo) {
        referenceVideo = asset
        validAssets.push(asset)
        continue
      }

      const { width, height, fpsNum, fpsDenum, videoEncoderParams,
        videoEncoderParsedParams, codecsInfo, dfFlag, colorPrimaries } = referenceVideo

      if (settings[BehaviourSettings.preventVideoAddToTimelineOnMismatch]) {
        const refCodec = getVideoCodec(codecsInfo)
        const assetCodec = getVideoCodec(asset.codecsInfo)

        if (assetCodec !== refCodec) {
          addSkipped(asset, 'MESSAGE_BOX_VIDEO_CODECS_MISMATCH')
          continue
        }

        if (asset.height !== height || asset.width !== width) {
          addSkipped(asset, 'MESSAGE_BOX_VIDEO_RESOLUTION_MISMATCH')
          continue
        }

        if (asset.fpsDenum !== fpsDenum || asset.fpsNum !== fpsNum) {
          addSkipped(asset, 'MESSAGE_BOX_VIDEO_FPS_MISMATCH')
          continue
        }

        if (asset.dfFlag !== dfFlag) {
          addSkipped(asset, 'MESSAGE_BOX_VIDEO_DF_NDF_TIMECODE_FORMAT_MISMATCH')
          continue
        }

        if (asset.colorPrimaries !== colorPrimaries) {
          addSkipped(asset, 'MESSAGE_BOX_VIDEO_COLOR_SPACE_MISMATCH')
          continue
        }
      }

      if (settings[BehaviourSettings.preventAddVideoFilesWDiffVideoEncoderParams]) {
        let hasEncoderMismatch = false

        if (videoEncoderParsedParams
          && settings[BehaviourSettings.ignoreQuantizerVideoParamsMismatch]) {
          try {
            const ignored = new Set([
              'chroma_qp_index_offset', 'pic_scaling_matrix_present_flag', 'video_signal_type_present_flag',
              'video_format', 'video_full_range_flag', 'colour_description_present_flag',
              'num_units_in_tick', 'time_scale', 'load_intra_quantiser_matrix', 'intra_quantizer_matrix',
              'load_non_intra_quantiser_matrix', 'non_intra_quantizer_matrix', 'colour_primaries',
              'transfer_characteristics', 'matrix_coefficients',
            ])
            const videoEncoderParamsObj = JSON.parse(videoEncoderParsedParams)

            if (asset.videoEncoderParsedParams) {
              const assetParamsObj = JSON.parse(asset.videoEncoderParsedParams)
              if (deepEqual(assetParamsObj, videoEncoderParamsObj, ignored) === false) {
                hasEncoderMismatch = true
              }
            }
          } catch (e) {
            console.error('videoEncoderParsedParams compare failed \n', e)
            hasEncoderMismatch = true
          }
        } else if (videoEncoderParams
          && asset.videoEncoderParams && asset.videoEncoderParams !== videoEncoderParams) {
          hasEncoderMismatch = true
        }

        if (hasEncoderMismatch) {
          addSkipped(asset, 'MESSAGE_BOX_VIDEO_ENCODER_PARAMS_MISMATCH')
          continue
        }
      }

      validAssets.push(asset)
    }

    const allValid = skippedAssets.length === 0

    if (!allValid) {
      if (assets.length === 1) {
        onDenied(skippedAssets[0].reason)
      } else {
        onDenied(t('MESSAGE_BOX_INCOMPATIBLE'))
      }
    }

    return {
      canAdd: validAssets.length > 0,
      allValid,
      validAssets,
      skippedAssets,
      skippedSummary,
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ allAssets, settings, t, onDenied ])
}
